import { RoundRequirement } from '../types'

export const getRecommendationsSummaryPart1 = (appRound: RoundRequirement) => {
  const maxRecs = appRound.recommendations.max
  const minRecs = appRound.recommendations.min

  if (minRecs === maxRecs && minRecs > 0) {
    return `Applicants will be asked to provide ${minRecs} recommendation${minRecs > 1 ? 's' : ''}.`
  }

  if (minRecs === 0 && maxRecs > 0) {
    return `Applicants will be asked to provide up to ${maxRecs} recommendation${maxRecs > 1 ? 's' : ''}.`
  }

  if (minRecs > 0) {
    return `Applicants will be asked to provide at least ${minRecs} and up to ${maxRecs} recommendations.`
  }

  return "Applicants won't be required to provide recommendations."
}

export const getRecommendationsSummaryPart2 = (appRound: RoundRequirement) => {
  const maxRecs = appRound.recommendations.max
  const minRecs = appRound.recommendations.min

  if (maxRecs === 0 && minRecs === 0) {
    return ''
  }

  const teacherRecs = appRound.recommendations.teacher.RequiredCount
  const counselorRecs = appRound.recommendations.counselor.RequiredCount
  const otherRecs = appRound.recommendations.other.RequiredCount
  const numberOfRequiredTypes = [teacherRecs > 0, counselorRecs > 0, otherRecs > 0].filter((val) => val).length

  const initialClause = (name: string, count: number) => {
    return count > 1 ? `${count} recommendations from ${name}s` : `1 recommendation from a ${name}`
  }

  const additionalClause = (name: string, count: number) => {
    return count > 1
      ? `${count} from ${name === 'other' ? 'other source' : name}s`
      : `1 from ${name === 'other' ? 'another source' : `a ${name}`}`
  }

  if (numberOfRequiredTypes === 3) {
    return `This includes ${initialClause('teacher', teacherRecs)}, ${additionalClause(
      'counselor',
      counselorRecs,
    )}, and ${additionalClause('other', otherRecs)}.`
  }

  if (numberOfRequiredTypes === 2) {
    if (teacherRecs > 0 && counselorRecs > 0) {
      return `This includes ${initialClause('teacher', teacherRecs)} and ${additionalClause(
        'counselor',
        counselorRecs,
      )}.`
    }

    if (teacherRecs > 0 && otherRecs > 0) {
      return `This includes ${initialClause('teacher', teacherRecs)} and ${additionalClause('other', otherRecs)}.`
    }

    return `This includes ${initialClause('counselor', counselorRecs)} and ${additionalClause('other', otherRecs)}.`
  }

  if (numberOfRequiredTypes === 1) {
    const oneRecTypeSentence = (recs: number, recType: string) => {
      if (recs < maxRecs) {
        return `${recs} of these must be provided by ${recType}.`
      }

      if (maxRecs > 1) {
        return `These must be provided by ${recType}.`
      }

      return `This must be provided by ${recType}.`
    }

    if (teacherRecs > 0) {
      return oneRecTypeSentence(teacherRecs, 'a teacher')
    }

    if (counselorRecs > 0) {
      return oneRecTypeSentence(counselorRecs, 'a counselor')
    }

    return oneRecTypeSentence(otherRecs, 'a source other than a counselor or teacher')
  }

  return ''
}

export const getRecommendationsSummaryPart3 = (appRound: RoundRequirement) => {
  const allowAdditionalTeacher = appRound.recommendations.teacher.OptionalSendStatus === 'AsAvailable'
  const allowAdditionalCounselor = appRound.recommendations.counselor.OptionalSendStatus === 'AsAvailable'
  const allowAdditionalOther = appRound.recommendations.other.OptionalSendStatus === 'AsAvailable'
  const maxRecs = appRound.recommendations.max
  const teacherRecs = appRound.recommendations.teacher.RequiredCount
  const counselorRecs = appRound.recommendations.counselor.RequiredCount
  const otherRecs = appRound.recommendations.other.RequiredCount
  const totalRequiredRecs = teacherRecs + counselorRecs + otherRecs
  const totalAdditionalRecsAllowed = maxRecs - totalRequiredRecs
  const numberOfAdditionalTypesAllowed = [
    allowAdditionalTeacher,
    allowAdditionalCounselor,
    allowAdditionalOther,
  ].filter((val) => val).length

  if (totalRequiredRecs === 0) {
    if (numberOfAdditionalTypesAllowed === 3) {
      return maxRecs > 1
        ? 'These can be provided by teachers, counselors, or other sources.'
        : 'This can be provided by a teacher, counselor, or other source.'
    }

    if (numberOfAdditionalTypesAllowed === 2) {
      if (allowAdditionalTeacher && allowAdditionalCounselor) {
        return maxRecs > 1
          ? 'These can be provided by either teachers or counselors.'
          : 'This can be provided by either a teacher or counselor.'
      }

      if (allowAdditionalTeacher && allowAdditionalOther) {
        return maxRecs > 1
          ? 'These can be provided by either teachers or other sources.'
          : 'This can be provided by either a teacher or another source.'
      }

      return maxRecs > 1
        ? 'These can be provided by either counselors or other sources.'
        : 'This can be provided by either a counselor or another source.'
    }

    if (numberOfAdditionalTypesAllowed === 1) {
      if (allowAdditionalTeacher) {
        return maxRecs > 1 ? 'These must be provided by a teacher.' : 'This must be provided by a teacher.'
      }

      if (allowAdditionalCounselor) {
        return maxRecs > 1 ? 'These must be provided by a counselor.' : 'This must be provided by a counselor.'
      }

      return maxRecs > 1
        ? 'These must be provided by a source other than a counselor or teacher.'
        : 'This must be provided by a source other than a counselor or teacher.'
    }
  } else {
    if (numberOfAdditionalTypesAllowed === 3) {
      return totalAdditionalRecsAllowed > 1
        ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from teachers, counselors, or other sources.`
        : 'Applicants can provide an additional recommendation from a teacher, counselor, or other source.'
    }

    if (numberOfAdditionalTypesAllowed === 2) {
      if (allowAdditionalTeacher && allowAdditionalCounselor) {
        return totalAdditionalRecsAllowed > 1
          ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from teachers or counselors.`
          : 'Applicants can provide an additional recommendation from either a teacher or counselor.'
      }

      if (allowAdditionalTeacher && allowAdditionalOther) {
        return totalAdditionalRecsAllowed > 1
          ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from teachers or other sources.`
          : 'Applicants can provide an additional recommendation from either a teacher or other source.'
      }

      return totalAdditionalRecsAllowed > 1
        ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from counselors or other sources.`
        : 'Applicants can provide an additional recommendation from either a counselor or other source.'
    }

    if (numberOfAdditionalTypesAllowed === 1) {
      if (allowAdditionalTeacher) {
        return totalAdditionalRecsAllowed > 1
          ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from a teacher.`
          : 'Applicants can provide an additional recommendation from a teacher.'
      }

      if (allowAdditionalCounselor) {
        return totalAdditionalRecsAllowed > 1
          ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from a counselor.`
          : 'Applicants can provide an additional recommendation from a counselor.'
      }

      return totalAdditionalRecsAllowed > 1
        ? `Applicants can provide up to ${totalAdditionalRecsAllowed} additional recommendations from a source other than a counselor or teacher.`
        : 'Applicants can provide an additional recommendation from a source other than a counselor or teacher.'
    }
  }

  return ''
}
const recommendationRequirementsForRound = (appRound: RoundRequirement) =>
  `${getRecommendationsSummaryPart1(appRound)} ${getRecommendationsSummaryPart2(
    appRound,
  )} ${getRecommendationsSummaryPart3(appRound)}`
export default recommendationRequirementsForRound
